import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";

import SubpageHeader from "../components/SubpageHeader";
import SiteFooter from "../components/SiteFooter";
import ArchiveTasteSearchPromo from "../components/ArchiveTasteSearchPromo";
import TasteTag from "../components/ui/TasteTag";
import Button from "../components/ui/Button";
import PosterPlaceholder from "../components/ui/PosterPlaceholder";
import Typography from "../components/ui/Typography";
import LoadingSpinner from "../components/LoadingSpinner";
import ErrorAlert from "../components/ErrorAlert";
import WatchlistHeartButton from "../components/ui/WatchlistHeartButton";
import BridgeText from "../components/BridgeText";
import usePageMeta from "../hooks/usePageMeta";
import useWatchlist from "../hooks/useWatchlist";
import { cn } from "../utils/classNames";
import { handleInternalLinkClick, navigateTo } from "../utils/navigation";
import { resolveInitialPayload } from "../utils/initialPayload";
import { fetchShowsLikeBySlug } from "../services/backendApi";
import { formatDisplayDate } from "../utils/formatDisplayDate";
import { getDisplayPlatforms } from "../utils/networkNames";
import { buildShowsLikeSeoMeta } from "./shows-like/showsLikeSeo";
import {
  buildArchiveTastePrompts,
  BROADER_CATALOG_PICK_COPY,
  buildShowsLikeLeadText,
} from "./shows-like/showsLikeCopy";

function getPosterUrl(show = {}) {
  return show?.posterMetadata?.thumbnail || show?.poster || null;
}

const ShowsLikePoster = ({ show, loading = "lazy", className = "" }) => {
  const posterUrl = getPosterUrl(show);
  const [failedPosterSrc, setFailedPosterSrc] = useState(null);
  const showPlaceholder = !posterUrl || failedPosterSrc === posterUrl;
  const showId = show?.slug || show?.id || show?.title;

  if (showPlaceholder) {
    return (
      <PosterPlaceholder
        showId={showId}
        className={cn("h-full w-full bg-poster-placeholder-gradient", className)}
      />
    );
  }

  return (
    <img
      src={posterUrl}
      alt={`${show.title} poster`}
      loading={loading}
      onError={() => setFailedPosterSrc(posterUrl)}
      className={cn("h-full w-full object-cover", className)}
    />
  );
};

ShowsLikePoster.propTypes = {
  show: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    slug: PropTypes.string,
    title: PropTypes.string,
    poster: PropTypes.string,
    posterMetadata: PropTypes.object,
  }).isRequired,
  loading: PropTypes.oneOf(["eager", "lazy"]),
  className: PropTypes.string,
};

function formatRating(rating) {
  return typeof rating === "number" && Number.isFinite(rating) ? rating.toFixed(1) : null;
}

function formatPlatformAndDate(show = {}) {
  const platform = getDisplayPlatforms(show)
    || (typeof show.sourceLabel === "string" ? show.sourceLabel.trim() : "")
    || null;
  const date = formatDisplayDate(show.premiereDate || show.releaseDate);
  if (platform && date) return `${platform} · ${date}`;
  return platform || date || null;
}

function pickDisplayTags(show = {}, tier = "primary") {
  if (tier === "secondary") {
    const shared = Array.isArray(show.sharedTasteTags) ? show.sharedTasteTags : [];
    if (shared.length > 0) {
      return shared.slice(0, 3);
    }
    const angleTags = Array.isArray(show.angleTags) ? show.angleTags : [];
    return angleTags.slice(0, 3);
  }
  const distinctive = Array.isArray(show.distinctiveTasteTags) ? show.distinctiveTasteTags : [];
  if (distinctive.length > 0) return distinctive.slice(0, 2);
  const fallback = Array.isArray(show.sharedTasteTags) ? show.sharedTasteTags : [];
  return fallback.slice(0, 3);
}

const ShowsLikeStructuredData = ({ payload }) => {
  if (!payload?.anchor || !Array.isArray(payload?.picks) || payload.picks.length === 0) {
    return null;
  }

  const pageUrl = `https://premierelens.com/shows-like/${payload.slug}`;
  const anchorUrl = `https://premierelens.com/show/${payload.slug}`;
  const listId = `${pageUrl}#item-list`;
  const anchorId = `${anchorUrl}#tvseries`;

  const itemList = {
    "@id": listId,
    "@type": "ItemList",
    name: `Shows like ${payload.anchor.title}`,
    description: `Shows with a similar mood, humour or pace to ${payload.anchor.title}.`,
    url: pageUrl,
    itemListOrder: "https://schema.org/ItemListOrderAscending",
    numberOfItems: payload.picks.length,
    itemListElement: payload.picks.map((pick, index) => ({
      "@type": "ListItem",
      position: index + 1,
      url: `https://premierelens.com/show/${pick.slug}`,
      item: {
        "@type": "TVSeries",
        name: pick.title,
        url: `https://premierelens.com/show/${pick.slug}`,
        ...(pick.bridge ? { description: pick.bridge } : {}),
        ...(getPosterUrl(pick) ? { image: getPosterUrl(pick) } : {}),
        ...(Array.isArray(pick.genres) && pick.genres.length > 0 ? { genre: pick.genres.slice(0, 5) } : {}),
      },
    })),
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "CollectionPage",
        "@id": `${pageUrl}#webpage`,
        name: `Shows like ${payload.anchor.title}`,
        url: pageUrl,
        about: {
          "@id": anchorId,
        },
        mainEntity: {
          "@id": listId,
        },
      },
      {
        "@type": "TVSeries",
        "@id": anchorId,
        name: payload.anchor.title,
        url: anchorUrl,
        ...(payload.anchor.plot ? { description: payload.anchor.plot } : {}),
        ...(getPosterUrl(payload.anchor) ? { image: getPosterUrl(payload.anchor) } : {}),
        ...(Array.isArray(payload.anchor.genres) && payload.anchor.genres.length > 0
          ? { genre: payload.anchor.genres.slice(0, 5) }
          : {}),
      },
      itemList,
      {
        "@type": "BreadcrumbList",
        "@id": `${pageUrl}#breadcrumb`,
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: "https://premierelens.com/",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: payload.anchor.title,
            item: anchorUrl,
          },
          {
            "@type": "ListItem",
            position: 3,
            name: `Shows like ${payload.anchor.title}`,
            item: pageUrl,
          },
        ],
      },
    ],
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
    />
  );
};

ShowsLikeStructuredData.propTypes = {
  payload: PropTypes.shape({
    slug: PropTypes.string,
    anchor: PropTypes.shape({
      title: PropTypes.string,
      plot: PropTypes.string,
      poster: PropTypes.string,
      posterMetadata: PropTypes.object,
      genres: PropTypes.arrayOf(PropTypes.string),
    }),
    picks: PropTypes.arrayOf(PropTypes.shape({
      slug: PropTypes.string,
      title: PropTypes.string,
      bridge: PropTypes.string,
      tier: PropTypes.string,
    })),
  }),
};

const ShowsLikeCard = ({ show, index, anchorTitle = "" }) => {
  const { isInWatchlist, toggleWatchlist } = useWatchlist();
  const href = `/show/${show.slug}`;
  const rating = formatRating(show.rating);
  const platformAndDate = formatPlatformAndDate(show);
  const tier = typeof show.tier === "string" ? show.tier : "primary";
  const tierLabel = show.tierLabel || "";
  const isLead = index === 0;
  const isSecondary = tier === "secondary";
  const posterGridClass = tier === "secondary"
    ? "grid-cols-[4.5rem_minmax(0,1fr)] sm:grid-cols-[5.5rem_minmax(0,1fr)]"
    : isLead
      ? "grid-cols-[4.5rem_minmax(0,1fr)] sm:grid-cols-[9rem_minmax(0,1fr)] md:grid-cols-[12rem_minmax(0,1fr)]"
      : "grid-cols-[4.5rem_minmax(0,1fr)] sm:grid-cols-[8rem_minmax(0,1fr)] md:grid-cols-[10rem_minmax(0,1fr)]";
  const displayTags = pickDisplayTags(show, tier);
  const tagLabel = tier === "secondary"
    ? (Array.isArray(show.sharedTasteTags) && show.sharedTasteTags.length > 0 ? "shared tags" : "tags")
    : "tags";
  const bridgeText = typeof show.bridge === "string" && show.bridge.trim() ? show.bridge.trim() : "";

  return (
    <article className={cn(
      "grid gap-4 rounded-lg border border-border/80 bg-card/70 p-4 shadow-xs shadow-black/5 transition-shadow duration-normal ease-out hover:border-accent-gold/70 hover:shadow-md md:gap-5",
      posterGridClass,
      isSecondary ? "border-dashed bg-card/60" : "md:p-5"
    )}>
      <a
        href={href}
        onClick={(event) => handleInternalLinkClick(event, href)}
        className="shows-like-card__poster aspect-[2/3] self-start overflow-hidden rounded-xl shadow-card"
        aria-label={`Open ${show.title}`}
      >
        <ShowsLikePoster
          show={show}
          loading={index === 0 ? "eager" : "lazy"}
          className="transition-transform duration-normal ease-out hover:scale-105"
        />
      </a>

      <div className="shows-like-card__copy min-w-0 space-y-3">
        <div className="space-y-2">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 space-y-1">
              {tierLabel && <p className="text-caption font-semibold text-secondary-readable">{tierLabel}</p>}
              <Typography as="h3" variant={isSecondary ? "h4" : "h3"} className="leading-tight">
                <a href={href} onClick={(event) => handleInternalLinkClick(event, href)} className="title-link">
                  {show.title}
                </a>
              </Typography>
              {platformAndDate && (
                <p className="text-body-small text-muted-foreground">{platformAndDate}</p>
              )}
            </div>
            <WatchlistHeartButton
              show={show}
              inWatchlist={Boolean(isInWatchlist?.(show))}
              onToggle={toggleWatchlist}
            />
          </div>

          {rating && (
            <div className="text-body-small text-foreground/75">
              <span>{show.imdbRated ? "IMDb" : "Viewer rating"} {rating}</span>
            </div>
          )}
        </div>

        {bridgeText && !isSecondary ? (
          <p className="reading-measure text-body leading-relaxed text-foreground/90">
            <span className="font-semibold text-foreground">Why it fits: </span>
            <BridgeText text={bridgeText} titles={[anchorTitle, show.title]} />
          </p>
        ) : isSecondary ? (
          <div className="shows-like-card__reason">
            <p className="text-body-small font-semibold text-primary">Why it fits</p>
            <p className="reading-measure pb-2 text-body-small leading-relaxed text-foreground/80">
              {bridgeText ? <BridgeText text={bridgeText} titles={[anchorTitle, show.title]} /> : BROADER_CATALOG_PICK_COPY}
            </p>
            {displayTags.length > 0 && (
              <ul className="flex flex-wrap gap-2" aria-label={`${show.title} ${tagLabel}`}>
                {displayTags.map((tag) => (
                  <li key={tag}><TasteTag variant="subtle">{tag}</TasteTag></li>
                ))}
              </ul>
            )}
          </div>
        ) : null}

        {displayTags.length > 0 && !isSecondary && (
          <ul
            className="flex flex-wrap gap-2"
            aria-label={`${show.title} ${tagLabel}`}
          >
            {displayTags.map((tag) => (
              <li key={tag}>
                <TasteTag variant={tier === "secondary" ? "subtle" : "default"}>
                  {tag}
                </TasteTag>
              </li>
            ))}
          </ul>
        )}

        {show.hasShowsLikePage && (
          <a
            href={`/shows-like/${show.slug}`}
            onClick={(event) => handleInternalLinkClick(event, `/shows-like/${show.slug}`)}
            className="inline-flex min-h-11 items-center text-body-small font-semibold text-primary underline-offset-4 hover:underline"
          >
            More shows like {show.title}
          </a>
        )}
      </div>
    </article>
  );
};

ShowsLikeCard.propTypes = {
  show: PropTypes.shape({
    slug: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    bridge: PropTypes.string,
    tier: PropTypes.string,
    tierLabel: PropTypes.string,
    sharedTasteTags: PropTypes.arrayOf(PropTypes.string),
    angleTags: PropTypes.arrayOf(PropTypes.string),
    distinctiveTasteTags: PropTypes.arrayOf(PropTypes.string),
    poster: PropTypes.string,
    posterMetadata: PropTypes.object,
    sourceLabel: PropTypes.string,
    sourceName: PropTypes.string,
    releaseDate: PropTypes.string,
    premiereDate: PropTypes.string,
    rating: PropTypes.number,
    imdbRated: PropTypes.bool,
    hasShowsLikePage: PropTypes.bool,
  }).isRequired,
  index: PropTypes.number.isRequired,
  anchorTitle: PropTypes.string,
};

const ShowsLikePage = ({ slug, initialShowsLikePayload = null }) => {
  const initialPayload = useMemo(
    () => resolveInitialPayload(initialShowsLikePayload, "slug", slug),
    [initialShowsLikePayload, slug]
  );
  const [payload, setPayload] = useState(() => initialPayload);
  const [loading, setLoading] = useState(() => !initialPayload);
  const [error, setError] = useState("");
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (initialPayload) {
      return undefined;
    }

    let active = true;
    const run = async () => {
      try {
        setLoading(true);
        const nextPayload = await fetchShowsLikeBySlug(slug);
        if (!active) return;
        if (nextPayload?.notFound || !nextPayload?.anchor || !Array.isArray(nextPayload?.picks)) {
          setNotFound(true);
          setPayload(null);
          return;
        }
        setPayload(nextPayload);
        setNotFound(false);
        setError("");
      } catch (nextError) {
        if (!active) return;
        setError(nextError.message || "Failed to load similar shows.");
      } finally {
        if (active) setLoading(false);
      }
    };

    run();
    return () => {
      active = false;
    };
  }, [initialPayload, slug]);

  const seoMeta = useMemo(
    () => (payload ? buildShowsLikeSeoMeta(payload) : null),
    [payload]
  );
  const metaTitle = seoMeta?.title || "Shows Like | PremiereLens";
  const metaDescription = seoMeta?.description || "Find more TV shows that match your taste on PremiereLens.";

  usePageMeta({
    title: metaTitle,
    description: metaDescription,
    pathname: `/shows-like/${slug}`,
    image: seoMeta?.image,
    noindex: notFound,
    ogType: seoMeta?.ogType,
  });

  const inboundSources = Array.isArray(payload?.inboundContext?.sources) ? payload.inboundContext.sources : [];
  const showBanner = Boolean(payload?.inboundContext?.count >= 4 && inboundSources.length >= 2);
  const [firstInboundSource, secondInboundSource] = inboundSources;
  const lead = buildShowsLikeLeadText();
  const archivePrompts = useMemo(
    () => (payload?.anchor ? buildArchiveTastePrompts(payload.anchor, payload) : []),
    [payload]
  );
  const pickCount = Array.isArray(payload?.picks) ? payload.picks.length : 0;
  const firstPick = Array.isArray(payload?.picks) && payload.picks.length > 0 ? payload.picks[0] : null;
  const resultsHeading = pickCount === 3 ? "Three shows to try next" : `${pickCount} shows to try next`;

  if (loading) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <SubpageHeader backHref="/" backLabel="Home" />
        <main id="main-content" tabIndex={-1} className="route-shell route-shell--standard">
          <div className="flex justify-center py-16">
            <LoadingSpinner />
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <SubpageHeader backHref="/" backLabel="Home" />
        <main id="main-content" tabIndex={-1} className="route-shell route-shell--standard">
          <div className="mx-auto max-w-2xl">
            <ErrorAlert message={error} />
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  if (notFound || !payload?.anchor || !Array.isArray(payload?.picks)) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <SubpageHeader backHref="/" backLabel="Home" />
        <main id="main-content" tabIndex={-1} className="route-shell route-shell--standard">
          <div className="mx-auto max-w-2xl py-16 text-center">
            <Typography variant="h2" className="mb-4 text-foreground">
              Shows Like Not Found
            </Typography>
            <Typography variant="body" className="mb-6 text-muted-foreground">
              We couldn&apos;t find a generated shows-like page for this URL.
            </Typography>
            <Button variant="default" onClick={() => navigateTo("/")}>
              Back to Home
            </Button>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SubpageHeader backHref={`/show/${slug}`} backLabel="Show" />
      <ShowsLikeStructuredData payload={payload} />

      <main id="main-content" tabIndex={-1} className="route-shell route-shell--media">
        <div className="route-shell__media-inner space-y-8 md:space-y-12">
          <section className="shows-like-hero" aria-labelledby="shows-like-hero-heading">
            <div className="shows-like-hero__copy">
              <Typography
                as="h1"
                id="shows-like-hero-heading"
                variant="h2"
                className="shows-like-hero__title sm:text-display"
              >
                Shows like{" "}
                <a href={`/show/${slug}`} onClick={(event) => handleInternalLinkClick(event, `/show/${slug}`)} className="title-link">
                  {payload.anchor.title}
                </a>
              </Typography>
              <Typography variant="body" className="shows-like-hero__lead">
                {lead}
              </Typography>

              {Array.isArray(payload?.tasteTags) && payload.tasteTags.length > 0 && (
                <ul className="shows-like-hero__tags" aria-label={`${payload.anchor.title} tags`}>
                  {payload.tasteTags.map((tag) => (
                    <li key={tag}>
                      <TasteTag>
                        {tag}
                      </TasteTag>
                    </li>
                  ))}
                </ul>
              )}

              <div className="shows-like-hero__actions">
                {firstPick && (
                  <Button asChild size="sm" className="shows-like-hero__primary-action">
                    <a href="#shows-like-first-pick">
                      Start with {firstPick.title}
                    </a>
                  </Button>
                )}
              </div>
            </div>

            <div className="shows-like-hero__media" aria-label={`${payload.anchor.title} poster`}>
              <a
                href={`/show/${slug}`}
                onClick={(event) => handleInternalLinkClick(event, `/show/${slug}`)}
                className="block h-full w-full"
                aria-label={`Open ${payload.anchor.title}`}
              >
                <ShowsLikePoster
                  show={payload.anchor}
                  loading="eager"
                  className="transition-transform duration-normal ease-out hover:scale-105"
                />
              </a>
            </div>

            {showBanner && (
              <aside className="shows-like-hero__proof" aria-label="Where else this show is recommended">
                <p className="shows-like-hero__proof-label">
                  Also recommended from
                </p>
                <p className="shows-like-hero__proof-copy">
                  {payload.anchor.title} appears among the picks for {payload.inboundContext.count} shows, including{" "}
                  <a
                    href={`/show/${firstInboundSource.slug}`}
                    onClick={(event) => handleInternalLinkClick(event, `/show/${firstInboundSource.slug}`)}
                  >
                    {firstInboundSource.title}
                  </a>
                  {" and "}
                  <a
                    href={`/show/${secondInboundSource.slug}`}
                    onClick={(event) => handleInternalLinkClick(event, `/show/${secondInboundSource.slug}`)}
                  >
                    {secondInboundSource.title}
                  </a>
                  .
                </p>
              </aside>
            )}
          </section>

          <section aria-labelledby="shows-like-results-heading" className="space-y-4">
            <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-end">
              <div className="space-y-1">
                <Typography as="h2" id="shows-like-results-heading" variant="h3">
                  {resultsHeading}
                </Typography>
                <Typography variant="body-small" className="max-w-[62ch] text-foreground/75">
                  Closest matches first, then broader nearby picks.
                </Typography>
              </div>
            </div>

            <ol className="space-y-4">
              {payload.picks.map((pick, index) => (
                <li key={pick.slug} id={index === 0 ? "shows-like-first-pick" : undefined}>
                  <ShowsLikeCard show={pick} index={index} anchorTitle={payload.anchor.title} />
                </li>
              ))}
            </ol>
          </section>

          <section className="space-y-4">
            <ArchiveTasteSearchPromo surfaceKey="shows-like-bottom" prompts={archivePrompts} />
          </section>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
};

ShowsLikePage.propTypes = {
  slug: PropTypes.string.isRequired,
  initialShowsLikePayload: PropTypes.shape({
    slug: PropTypes.string,
    anchor: PropTypes.object,
    picks: PropTypes.arrayOf(PropTypes.object),
    inboundContext: PropTypes.shape({
      count: PropTypes.number,
      sources: PropTypes.arrayOf(PropTypes.object),
    }),
    tasteTags: PropTypes.arrayOf(PropTypes.string),
    lastUpdated: PropTypes.string,
  }),
};

export default ShowsLikePage;
